import { useAccount } from 'wagmi'

import { SignersList } from '~/components/SignersList'
import { ThresholdSelect } from '~/components/account-detail/AccountActionsMenu/ThresholdSelect'
import { useSaveSmartAccountSigners } from '~/components/account-detail/AccountActionsMenu/useSaveSmartAccountSigners'
import { useSignerSelection } from '~/components/account-detail/AccountActionsMenu/useSignerSelection'
import { ConnectedWallet } from '~/components/auth/ConnectedWallet'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { OrgSmartAccount } from '~/types'
import { sendTrackingData } from '~/utils/tracking'

export const ResetSignersDialog = ({
  smartAccount,
  closeDialog,
}: {
  smartAccount: OrgSmartAccount
  closeDialog: () => void
}) => {
  const { save, isProcessing } = useSaveSmartAccountSigners({
    smartAccount,
    isRecoveryTx: true,
  })
  const { address: connectedAddress } = useAccount()
  const {
    org,
    selectedSigners,
    threshold,
    setThreshold,
    availableSigners,
    setSigner,
    handleAddNewPasskey,
  } = useSignerSelection({
    baseSigners: [],
    initialSigners: [],
    initialThreshold: 1,
  })

  const onSave = async () => {
    sendTrackingData('clickedRecoverSmartAccount', {
      smartAccountId: smartAccount.id,
    })
    const result = await save({ signers: selectedSigners, threshold })
    if (result) closeDialog()
  }

  const thresholdTooHigh =
    selectedSigners.length > 0 && threshold > selectedSigners.length
  const isSaveDisabled =
    selectedSigners.length === 0 ||
    threshold < 1 ||
    thresholdTooHigh ||
    isProcessing

  return (
    <SplitsDialogContent
      id="recoverSmartAccount"
      title={`Reset ${smartAccount.name} signers`}
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        text: 'Save',
        onClick: () => void onSave(),
        isDisabled: isSaveDisabled,
        disabledReason: thresholdTooHigh
          ? 'Your threshold cannot be greater than the number of selected signers'
          : undefined,
      }}
    >
      {!!connectedAddress && <ConnectedWallet />}
      <SignersList
        header="Select passkeys"
        signers={availableSigners}
        eoaSigners={[]}
        initialExpanded={org?.members.length === 1}
        editable={{ selectedSigners, onChange: setSigner }}
        onAddNewPasskey={handleAddNewPasskey}
      />
      <ThresholdSelect
        signerCount={selectedSigners.length}
        threshold={threshold}
        setThreshold={setThreshold}
      />
    </SplitsDialogContent>
  )
}
